import React, { useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Building2, Plus, X, Copy, KeyRound, CheckCircle2, PauseCircle, PlayCircle, Users, Wallet, Clock,
  BarChart3, Download, Lock, ChevronDown, ChevronRight, AlertTriangle, Sparkles, Loader2,
} from "lucide-react";

const usd2 = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="kpi-label">{label}</span>
    {children}
    {hint && <span className="block text-[11px] text-ink-3 mt-1">{hint}</span>}
  </label>
);

const inputCls = "w-full mt-1 px-3 py-2.5 rounded-xl text-sm";
const inputStyle = { border: "1.5px solid rgba(15,18,38,0.12)" };

function TermsFields({ terms, setTerms }) {
  const set = (k) => (e) => setTerms({ ...terms, [k]: e.target.value });
  return (
    <div className="grid grid-cols-2 gap-3">
      <Field label="Partner's share of the margin %" hint="Of everything above the client promise">
        <input value={terms.margin_split_partner_pct} onChange={set("margin_split_partner_pct")}
          className={inputCls} style={inputStyle} data-testid="terms-split" />
      </Field>
      <Field label="Promise range they may offer (% / month)">
        <div className="flex items-center gap-2 mt-1">
          <input value={terms.promise_min_pct} onChange={set("promise_min_pct")} className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} data-testid="terms-promise-min" />
          <span className="text-ink-3">–</span>
          <input value={terms.promise_max_pct} onChange={set("promise_max_pct")} className="w-full px-3 py-2.5 rounded-xl text-sm" style={inputStyle} data-testid="terms-promise-max" />
        </div>
      </Field>
      <Field label="Minimum ticket (USDT)">
        <input value={terms.min_ticket} onChange={set("min_ticket")} className={inputCls} style={inputStyle} data-testid="terms-min-ticket" />
      </Field>
      <Field label="Maximum ticket (USDT)">
        <input value={terms.max_ticket} onChange={set("max_ticket")} className={inputCls} style={inputStyle} data-testid="terms-max-ticket" />
      </Field>
    </div>
  );
}

function CreateModal({ defaults, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [terms, setTerms] = useState(defaults.terms);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  // No membership account, no partner. The firm must belong to a BotChainCP member
  // who has been ticked as Partner in Members — that's the whole mapping.
  const [eligible, setEligible] = useState(null);
  const [memberId, setMemberId] = useState("");

  useEffect(() => {
    api.get("/partners/eligible-members")
      .then(({ data }) => setEligible(data.members || []))
      .catch(() => setEligible([]));
  }, []);

  const member = (eligible || []).find((m) => m.id === memberId) || null;
  useEffect(() => {
    if (!member) return;
    setContactName((v) => v || member.name || "");
    setContactEmail((v) => v || member.email || "");
  }, [member]);

  const submit = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try {
      const { data } = await api.post("/partners", {
        name, slug: slug || undefined, contact_name: contactName || undefined,
        contact_email: contactEmail || undefined, login_email: loginEmail || undefined,
        member_user_id: memberId || undefined,
        terms: {
          margin_split_partner_pct: Number(terms.margin_split_partner_pct),
          promise_min_pct: Number(terms.promise_min_pct),
          promise_max_pct: Number(terms.promise_max_pct),
          min_ticket: Number(terms.min_ticket), max_ticket: Number(terms.max_ticket),
        },
      });
      setCreated(data);
      onCreated();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="partner-create-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <form onSubmit={submit} className="relative w-full max-w-[560px] rounded-2xl overflow-hidden fade-up max-h-[90vh] overflow-y-auto"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        <div className="flex items-center gap-3 px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
          <Building2 size={18} style={{ color: "#4060FF" }} />
          <h3 className="font-display text-xl font-semibold tracking-tight">New partner</h3>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5" data-testid="partner-create-close"><X size={16} /></button>
        </div>

        {created ? (
          <div className="px-6 py-6 space-y-3" data-testid="partner-created">
            <div className="flex items-center gap-2 text-[15px] font-semibold"><CheckCircle2 size={18} style={{ color: "#0FB999" }} /> {created.partner.name} is live</div>
            <div className="text-sm text-ink-2">Portal address: <b>/p/{created.partner.slug}</b></div>
            {created.login && (
              <div className="glass p-3.5 text-sm" style={{ border: "1.5px solid rgba(64,96,255,0.35)" }}>
                <div className="font-semibold mb-1">Partner login</div>
                <div className="font-mono text-[12.5px]">{created.login.email}</div>
                <div className="font-mono text-[12.5px]" data-testid="partner-temp-password">{created.login.temp_password}</div>
                <div className="text-[11px] text-ink-2 mt-1.5">Sign-in page: <span className="font-mono">{created.login.login_url}</span></div>
                <div className="text-[11px] text-ink-3 mt-0.5">
                  {created.login.emailed ? "Emailed to them as well." : "Email didn't go out — share this yourself."} Shown once.
                </div>
              </div>
            )}
            <button type="button" onClick={onClose} className="btn-primary" data-testid="partner-created-done">Done</button>
          </div>
        ) : (
          <>
            <div className="px-6 py-5 space-y-4">
              <Field label="Which member runs this firm?" hint="Only members ticked as Partner in Members appear here">
                {eligible === null ? (
                  <div className="text-[12px] text-ink-3 mt-1" data-testid="partner-member-loading">Looking up members…</div>
                ) : eligible.length === 0 ? (
                  <div className="mt-1 p-3 rounded-xl text-[11.5px] leading-relaxed"
                    style={{ background: "rgba(180,83,9,0.08)", color: "#B45309" }}
                    data-testid="partner-member-none">
                    Nobody is marked as a partner yet. Open <b>Members</b>, tick <b>Partner</b> against
                    the member who runs the firm, then come back — a partner always has a membership account.
                  </div>
                ) : (
                  <select value={memberId} onChange={(e) => setMemberId(e.target.value)}
                    className={inputCls} style={inputStyle} data-testid="partner-member-select">
                    <option value="">Choose a member…</option>
                    {eligible.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.email}{m.name ? ` · ${m.name}` : ""}{m.firms?.length ? ` · already runs ${m.firms.length}` : ""}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              {member && (
                <div className="glass p-3 text-[12px]" data-testid="partner-member-picked">
                  <b>{member.email}</b> signs in to BotChainCP exactly as they do now — same password — and
                  lands in this firm&apos;s console. Their member dashboard stays a menu away.
                  {member.firms?.length ? (
                    <div className="mt-1 text-ink-3">Already runs: {member.firms.map((f) => f.name).join(", ")}</div>
                  ) : null}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <Field label="Organisation name">
                  <input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} style={inputStyle} placeholder="Northgate Capital" data-testid="partner-name" />
                </Field>
                <Field label="Portal address" hint="Just the last part — a whole address is fine too">
                  <input value={slug} onChange={(e) => setSlug(e.target.value)} className={inputCls} style={inputStyle} placeholder="northgate" data-testid="partner-slug" />
                </Field>
                <Field label="Contact name">
                  <input value={contactName} onChange={(e) => setContactName(e.target.value)} className={inputCls} style={inputStyle} data-testid="partner-contact-name" />
                </Field>
                <Field label="Contact email" hint="Gets the new-application alerts">
                  <input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} type="email" className={inputCls} style={inputStyle} data-testid="partner-contact-email" />
                </Field>
              </div>
              <Field label="Extra console login" hint="Optional — the member above already gets in with their own BotChainCP password. Add an email here only for a second person.">
                <input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} type="email" className={inputCls} style={inputStyle} data-testid="partner-login-email" />
              </Field>
              <div className="pt-1"><span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Commercials</span></div>
              <TermsFields terms={terms} setTerms={setTerms} />
              {error && <div className="chip chip-rose" data-testid="partner-create-error">{error}</div>}
            </div>
            <div className="px-6 pb-5 flex items-center gap-2 justify-end">
              <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={busy || !memberId} className="btn-primary disabled:opacity-40" data-testid="partner-create-submit"
                title={memberId ? "" : "Pick the member who runs this firm first"}>
                {busy ? "Creating…" : "Create partner"}
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}

function DetailModal({ id, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [memberOptions, setMemberOptions] = useState(null);
  const [linkId, setLinkId] = useState("");
  const [terms, setTerms] = useState(null);
  const [slug, setSlug] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [newLogin, setNewLogin] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/partners/${id}`);
    setD(data); setTerms(data.partner.terms); setSlug(data.partner.slug);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
  // Only fetched when the firm has nobody behind it — that's the only time it's offered.
  useEffect(() => {
    if (!d || d.partner.member_linked) return;
    api.get("/partners/eligible-members")
      .then(({ data }) => setMemberOptions(data.members || []))
      .catch(() => setMemberOptions([]));
  }, [d]);

  const saveSlug = async () => {
    try {
      await api.patch(`/partners/${id}`, { slug });
      toast.success("Portal address updated — the old one stops working");
      onChanged(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveTerms = async () => {
    setBusy(true);
    try {
      await api.patch(`/partners/${id}`, {
        terms: {
          margin_split_partner_pct: Number(terms.margin_split_partner_pct),
          promise_min_pct: Number(terms.promise_min_pct),
          promise_max_pct: Number(terms.promise_max_pct),
          min_ticket: Number(terms.min_ticket), max_ticket: Number(terms.max_ticket),
        },
      });
      toast.success("Commercials saved"); onChanged(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleStatus = async () => {
    const next = d.partner.status === "active" ? "suspended" : "active";
    try {
      await api.patch(`/partners/${id}`, { status: next });
      toast.success(next === "active" ? "Partner reactivated" : "Partner suspended");
      onChanged(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const linkMember = async () => {
    try {
      await api.post(`/partners/${id}/member`, { member_user_id: linkId });
      toast.success("Member attached — they can sign in to this console with their own password");
      setLinkId("");
      load();
      onChanged();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const addLogin = async () => {
    try {
      const { data } = await api.post(`/partners/${id}/logins`, { email: loginEmail });
      setNewLogin(data); setLoginEmail(""); onChanged(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const markFunded = async (depId) => {
    try {
      await api.post(`/partners/deposits/${depId}/status`, { status: "funded" });
      toast.success("Funds recorded"); onChanged(); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clientName = (cid) => (d?.clients || []).find((c) => c.id === cid)?.name || "—";

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 overflow-y-auto" data-testid="partner-detail-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[900px] rounded-2xl overflow-hidden fade-up my-6"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        {!d ? <div className="p-10 text-sm text-ink-2">Loading…</div> : (
          <>
            <div className="flex items-center gap-3 px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
              <div>
                <h3 className="font-display text-xl font-semibold tracking-tight">{d.partner.name}</h3>
                <div className="text-[12px] text-ink-3">/p/{d.partner.slug} · {d.partner.contact_email || "no contact email"}</div>
              </div>
              <span className={`chip ml-2 ${d.partner.status === "active" ? "chip-emerald" : "chip-rose"}`} data-testid="partner-status-chip">{d.partner.status}</span>
              <button onClick={toggleStatus} className="btn-secondary ml-auto" data-testid="partner-toggle-status">
                {d.partner.status === "active" ? <><PauseCircle size={14} /> Suspend</> : <><PlayCircle size={14} /> Reactivate</>}
              </button>
              <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5" data-testid="partner-detail-close"><X size={16} /></button>
            </div>

            <div className="px-6 py-5 space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[["Clients", d.partner.stats.clients], ["Awaiting review", d.partner.stats.applied],
                  ["Funded", usd(d.partner.stats.funded_usdt)], ["Awaiting funds", usd(d.partner.stats.awaiting_usdt)]].map(([k, v]) => (
                  <div key={k} className="glass p-3">
                    <div className="kpi-label">{k}</div>
                    <div className="font-display text-xl font-semibold">{v}</div>
                  </div>
                ))}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Portal address</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm text-ink-3 font-mono">/p/</span>
                  <input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="yourfirm"
                    className="px-3 py-2 rounded-xl text-sm font-mono flex-1 min-w-[200px]" style={inputStyle}
                    data-testid="partner-slug-input" />
                  <button onClick={saveSlug} disabled={!slug.trim() || slug === d.partner.slug}
                    className="btn-secondary" data-testid="partner-save-slug">Save address</button>
                  <a href={`/p/${d.partner.slug}`} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="partner-open-portal">Open portal</a>
                </div>
                <div className="text-[11px] text-ink-3 mt-1.5">
                  Their public page is <span className="font-mono">/p/{d.partner.slug}</span> and their clients sign in at{" "}
                  <span className="font-mono">/p/{d.partner.slug}/login</span>. Paste a whole address if you like — we&apos;ll take the last part.
                </div>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Commercials</div>
                {terms && <TermsFields terms={terms} setTerms={setTerms} />}
                <button onClick={saveTerms} disabled={busy} className="btn-primary mt-3" data-testid="partner-save-terms">Save commercials</button>
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Member behind this firm</div>
                {d.partner.member_linked ? (
                  <div className="glass p-3 text-sm" data-testid="partner-detail-member">
                    <div className="font-mono text-[12.5px]">{d.partner.member.email}</div>
                    <div className="text-[11px] text-ink-3 mt-0.5">
                      Signs in to BotChainCP with their own password and lands in this console.
                      {d.partner.member.last_login_at ? ` Last in ${String(d.partner.member.last_login_at).slice(0, 10)}.` : " Not signed in yet."}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2" data-testid="partner-detail-no-member">
                    <div className="p-3 rounded-xl text-[11.5px] leading-relaxed"
                      style={{ background: "rgba(180,83,9,0.08)", color: "#B45309" }}>
                      No member behind this firm. It keeps working, but a partner should always have a
                      BotChainCP membership account — attach the member who runs it.
                    </div>
                    {memberOptions === null ? (
                      <div className="text-[12px] text-ink-3">Looking up members…</div>
                    ) : memberOptions.length === 0 ? (
                      <div className="text-[12px] text-ink-3">
                        Nobody is marked as a partner yet — tick <b>Partner</b> against them in Members first.
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <select value={linkId} onChange={(e) => setLinkId(e.target.value)}
                          className="px-3 py-2 rounded-xl text-sm" style={inputStyle} data-testid="partner-link-member-select">
                          <option value="">Choose a member…</option>
                          {memberOptions.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}
                        </select>
                        <button onClick={linkMember} disabled={!linkId || busy} className="btn-secondary disabled:opacity-40"
                          data-testid="partner-link-member">Attach member</button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Console logins</div>
                <div className="space-y-1.5">
                  {(d.partner.logins || []).map((l) => (
                    <div key={l.id} className="flex items-center gap-2 text-sm" data-testid={`partner-login-${l.email}`}>
                      <KeyRound size={13} className="text-ink-3" /> <span className="font-mono text-[12.5px]">{l.email}</span>
                      <span className="text-[11px] text-ink-3">{l.last_login_at ? `last in ${String(l.last_login_at).slice(0, 10)}` : "never signed in"}</span>
                    </div>
                  ))}
                  {!(d.partner.logins || []).length && <div className="text-sm text-ink-3">No logins yet.</div>}
                </div>
                <div className="flex items-center gap-2 mt-2.5">
                  <input value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} placeholder="name@partner.com" type="email"
                    className="px-3 py-2 rounded-xl text-sm" style={inputStyle} data-testid="partner-new-login-email" />
                  <button onClick={addLogin} className="btn-secondary" data-testid="partner-add-login">Add login</button>
                </div>
                {newLogin && (
                  <div className="glass p-3 mt-2 text-sm" data-testid="partner-new-login-result">
                    <b>{newLogin.email}</b> · temporary password <span className="font-mono">{newLogin.temp_password}</span>
                    <div className="text-[11px] text-ink-2 mt-0.5">Sign-in page: <span className="font-mono">{newLogin.login_url}</span></div>
                    <div className="text-[11px] text-ink-3">{newLogin.emailed ? "Emailed too." : "Email failed — share it yourself."}</div>
                  </div>
                )}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Offers</div>
                {(d.offers || []).length ? (
                  <div className="space-y-1.5 text-sm">
                    {d.offers.map((o) => (
                      <div key={o.id} className="flex items-center gap-3" data-testid={`partner-offer-${o.id}`}>
                        <span className="font-semibold">{o.name}</span>
                        <span className="text-ink-2">{o.promised_rate_pct}% / month · {o.term_months} months · paid {o.payout_mode}</span>
                        <span className="text-ink-3 text-[12px]">{usd(o.min_ticket)}–{usd(o.max_ticket)}</span>
                        <span className={`chip ${o.status === "active" ? "chip-emerald" : ""}`}>{o.status}</span>
                      </div>
                    ))}
                  </div>
                ) : <div className="text-sm text-ink-3">The partner hasn't published an offer yet.</div>}
              </div>

              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3 mb-2">Commitments</div>
                {(d.deposits || []).length ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
                        <th className="py-1.5">Ref</th><th>Client</th><th>Amount</th><th>Promise</th><th>Status</th><th></th>
                      </tr></thead>
                      <tbody>
                        {d.deposits.map((dep) => (
                          <tr key={dep.id} className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`partner-deposit-${dep.reference}`}>
                            <td className="py-2 font-mono text-[12px]">{dep.reference}</td>
                            <td>{clientName(dep.client_id)}</td>
                            <td className="font-semibold">{usd(dep.amount)}</td>
                            <td className="text-ink-2">{dep.promised_rate_pct}% · {dep.term_months}m · {dep.payout_mode}</td>
                            <td><span className={`chip ${dep.status === "funded" ? "chip-emerald" : ""}`}>{dep.status.replace("_", " ")}</span></td>
                            <td className="text-right">
                              {dep.status === "pending_funds" && (
                                <button onClick={() => markFunded(dep.id)} className="btn-secondary" data-testid={`mark-funded-${dep.reference}`}>
                                  <Wallet size={13} /> Funds received
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="text-sm text-ink-3">No client commitments yet.</div>}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function PnlPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [open, setOpen] = useState(null);
  const [rate, setRate] = useState({});

  const load = async () => {
    try { setData((await api.get("/partners/reports/pnl?months=12")).data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const closeMonth = async (month) => {
    setBusy(month);
    try {
      const { data: r } = await api.post(`/partners/close/${month}`);
      toast.success(`${month} closed · ${r.booked} rows at ${r.gross_pct}% gross`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  const saveRate = async (month) => {
    try {
      await api.post(`/partners/gross-rate/${month}`, { pct: Number(rate[month]) });
      toast.success(`${month} gross yield set to ${rate[month]}%`);
      setRate({ ...rate, [month]: undefined }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const pay = async (month, partnerId, kind) => {
    try {
      const { data: r } = await api.post(`/partners/payouts/${month}`, { partner_id: partnerId, kind });
      toast.success(`${r.rows} row${r.rows === 1 ? "" : "s"} marked paid`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const csv = async () => {
    try {
      const res = await api.get("/partners/reports/pnl?months=12&csv=true", { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = "partner-pnl.csv";
      a.click();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  if (!data) return <div className="glass p-8 text-sm text-ink-2">Working out the numbers…</div>;
  const t = data.totals;

  return (
    <div className="space-y-4" data-testid="partners-pnl">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[["Capital under management", usd(t.capital)], ["Gross earned", usd(t.gross)],
          ["Owed to clients", usd(t.client)], ["Partner payouts", usd(t.partner)],
          ["Our net", usd(t.house)]].map(([k, v]) => (
          <div key={k} className="glass p-4" data-testid={`pnl-total-${k.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <div className="kpi-label">{k}</div>
            <div className="font-display text-xl font-semibold mt-1">{v}</div>
          </div>
        ))}
      </div>

      <div className="glass p-0 overflow-x-auto">
        <div className="flex items-center gap-3 px-4 pt-4 pb-2">
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">Month by month</span>
          <span className="text-[11.5px] text-ink-3">Gross yield is read from our own staked CaryPact book — override any month if reality differs.</span>
          <button onClick={csv} className="btn-secondary ml-auto" data-testid="pnl-csv"><Download size={13} /> CSV</button>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
            <th className="px-4 py-2.5">Month</th><th>Gross yield</th><th>Capital</th><th>Gross</th>
            <th>Client obligation</th><th>Partner share</th><th>Our net</th><th>State</th><th></th>
          </tr></thead>
          <tbody>
            {data.months.map((m) => (
              <React.Fragment key={m.month}>
                <tr className="border-t" style={{ borderColor: "rgba(15,18,38,0.06)" }} data-testid={`pnl-row-${m.month}`}>
                  <td className="px-4 py-2.5 font-semibold">
                    <button onClick={() => setOpen(open === m.month ? null : m.month)} className="inline-flex items-center gap-1.5" data-testid={`pnl-expand-${m.month}`}>
                      {open === m.month ? <ChevronDown size={13} /> : <ChevronRight size={13} />} {m.month}
                    </button>
                  </td>
                  <td>
                    {rate[m.month] !== undefined ? (
                      <span className="inline-flex items-center gap-1.5">
                        <input value={rate[m.month]} onChange={(e) => setRate({ ...rate, [m.month]: e.target.value })}
                          className="w-16 px-2 py-1 rounded-lg text-sm" style={inputStyle} data-testid={`pnl-rate-input-${m.month}`} />
                        <button onClick={() => saveRate(m.month)} className="btn-secondary" data-testid={`pnl-rate-save-${m.month}`}>Save</button>
                      </span>
                    ) : (
                      <button onClick={() => setRate({ ...rate, [m.month]: String(m.gross_pct ?? "") })}
                        className="inline-flex items-center gap-1.5" title={`${m.gross_source === "override" ? "Set by hand" : `From ${m.staking_positions} live staking positions`}`}
                        data-testid={`pnl-rate-${m.month}`}>
                        <b>{m.gross_pct ?? "—"}%</b>
                        <span className="text-[10.5px] text-ink-3">{m.gross_source === "override" ? "manual" : "auto"}</span>
                      </button>
                    )}
                  </td>
                  <td>{usd(m.capital)}</td>
                  <td>{usd2(m.gross)}</td>
                  <td>{usd2(m.client_obligation)}</td>
                  <td>{usd2(m.partner_share)}</td>
                  <td className="font-semibold" style={{ color: m.house_net < 0 ? "#B91C1C" : "#0B8F78" }}>{usd2(m.house_net)}</td>
                  <td>
                    <span className={`chip ${m.booked ? "chip-emerald" : ""}`}>{m.booked ? "closed" : m.commitments ? "accruing" : "—"}</span>
                    {!!m.shortfall_rows && (
                      <span className="chip chip-rose ml-1" title="Months where the promise was above what the capital earned — we absorbed it">
                        <AlertTriangle size={11} /> {m.shortfall_rows}
                      </span>
                    )}
                  </td>
                  <td className="text-right px-4">
                    {!m.booked && !!m.commitments && m.month < new Date().toISOString().slice(0, 7) && (
                      <button onClick={() => closeMonth(m.month)} disabled={busy === m.month} className="btn-secondary" data-testid={`pnl-close-${m.month}`}>
                        <Lock size={13} /> {busy === m.month ? "Closing…" : "Close month"}
                      </button>
                    )}
                  </td>
                </tr>
                {open === m.month && (
                  <tr style={{ background: "rgba(15,18,38,0.02)" }}>
                    <td colSpan={9} className="px-4 py-3">
                      {!m.by_partner.length ? <span className="text-sm text-ink-3">No capital running this month.</span> : (
                        <table className="w-full text-sm">
                          <thead><tr className="text-left text-[11px] uppercase tracking-wider text-ink-3">
                            <th className="py-1">Partner</th><th>Clients</th><th>Capital</th><th>Gross</th>
                            <th>Client obligation</th><th>Their share</th><th>Our net</th><th></th>
                          </tr></thead>
                          <tbody>
                            {m.by_partner.map((b) => (
                              <tr key={b.partner_id} data-testid={`pnl-partner-${m.month}-${b.partner_id}`}>
                                <td className="py-1.5 font-semibold">{b.name}</td>
                                <td>{b.clients}</td><td>{usd(b.capital)}</td><td>{usd2(b.gross)}</td>
                                <td>{usd2(b.client)}</td><td>{usd2(b.partner)}</td><td>{usd2(b.house)}</td>
                                <td className="text-right">
                                  {m.booked && (
                                    <span className="inline-flex gap-1.5">
                                      <button onClick={() => pay(m.month, b.partner_id, "partner")} disabled={b.partner_paid}
                                        className="btn-secondary" data-testid={`pay-partner-${m.month}-${b.partner_id}`}>
                                        {b.partner_paid ? "Partner paid" : "Pay partner"}
                                      </button>
                                      <button onClick={() => pay(m.month, b.partner_id, "client")} disabled={b.client_paid}
                                        className="btn-secondary" data-testid={`pay-clients-${m.month}-${b.partner_id}`}>
                                        {b.client_paid ? "Clients paid" : "Clients paid?"}
                                      </button>
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DemoFirmModal({ onClose, onSeeded }) {
  const [busy, setBusy] = useState(false);
  const [out, setOut] = useState(null);
  const [error, setError] = useState("");

  const run = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post("/partners/demo/seed", {});
      setOut(data);
      onSeeded();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copyAll = () => {
    const lines = [
      `${out.partner.name}`,
      `Public site     ${out.portal_url}`,
      `Client sign-in  ${out.client_login_url}`,
      `Partner console ${out.console_url}  ${out.partner_login.email} / ${out.partner_login.password}`,
      `Every client    ${out.client_password}`,
      ...out.clients.map((c) => `  ${c.name} — ${c.email}`),
    ];
    navigator.clipboard?.writeText(lines.join("\n"));
    toast.success("Demo credentials copied");
  };

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" data-testid="demo-firm-modal">
      <div className="absolute inset-0" style={{ background: "rgba(15,18,38,0.42)", backdropFilter: "blur(4px)" }} onClick={onClose} />
      <div className="relative w-full max-w-[600px] rounded-2xl overflow-hidden fade-up max-h-[90vh] overflow-y-auto"
        style={{ background: "white", boxShadow: "0 30px 70px rgba(15,18,38,0.28)" }}>
        <div className="flex items-center gap-3 px-6 pt-5 pb-4" style={{ borderBottom: "1px solid rgba(15,18,38,0.07)" }}>
          <Sparkles size={18} style={{ color: "#C58B3C" }} />
          <h3 className="font-display text-xl font-semibold tracking-tight">Demo firm</h3>
          <button type="button" onClick={onClose} className="ml-auto w-8 h-8 rounded-lg flex items-center justify-center hover:bg-black/5" data-testid="demo-firm-close"><X size={16} /></button>
        </div>

        {out ? (
          <div className="px-6 py-5 space-y-3" data-testid="demo-firm-done">
            <div className="flex items-center gap-2 text-[15px] font-semibold">
              <CheckCircle2 size={18} style={{ color: "#0FB999" }} /> {out.partner.name} is ready
            </div>
            <div className="text-sm text-ink-2">
              {out.months_closed.length} months booked ({out.months_closed[0]} → {out.months_closed[out.months_closed.length - 1]}),
              {" "}{out.rows_booked} ledger rows and {out.clients.length} clients.
            </div>
            <div className="glass p-3.5 text-sm space-y-2" style={{ border: "1.5px solid rgba(197,139,60,0.4)" }}>
              <Row k="Public site" v={out.portal_url} />
              <Row k="Client sign-in" v={out.client_login_url} />
              <Row k="Partner console" v={`${out.partner_login.email} / ${out.partner_login.password}`} tid="demo-partner-credentials" />
              <Row k="Every client" v={out.client_password} tid="demo-client-password" />
            </div>
            <div className="space-y-1">
              {out.clients.map((c) => (
                <div key={c.email} className="text-[12.5px] flex items-center gap-2 flex-wrap" data-testid={`demo-client-${c.reference}`}>
                  <b>{c.name}</b>
                  <span className="font-mono text-ink-2">{c.email}</span>
                  <a href={c.statement_url} target="_blank" rel="noreferrer" className="text-[11.5px] underline text-ink-3">statement link</a>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={copyAll} className="btn-secondary" data-testid="demo-copy-credentials"><Copy size={13} /> Copy everything</button>
              <a href={out.portal_url} target="_blank" rel="noreferrer" className="btn-primary" data-testid="demo-open-portal">Open the demo site</a>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-3">
            <p className="text-sm text-ink-2">
              Builds <b>Ardenmoor Private Clients</b> — a branded site, three plans and five clients:
              one with nine months of payments, one paid at maturity, one paid quarterly, one matured
              and one still awaiting funds. The nine finished months are booked by the same engine that
              closes a real month, so every screen and every chart has real numbers in it.
            </p>
            <div className="glass p-3.5 text-[12.5px] text-ink-2 space-y-1.5">
              <div className="flex items-start gap-2"><AlertTriangle size={13} className="mt-0.5 shrink-0" style={{ color: "#B45309" }} />
                <span>Running this again wipes and rebuilds <b>only</b> this demo firm. Your real partners, their clients and the gross yields you've set are untouched.</span>
              </div>
              <div className="flex items-start gap-2"><Lock size={13} className="mt-0.5 shrink-0 text-ink-3" />
                <span>Its numbers do count in the P&amp;L, exactly like a real partner. Statement emails are switched off for it.</span>
              </div>
            </div>
            {error && <div className="chip chip-amber" data-testid="demo-firm-error">{error}</div>}
            <button type="button" onClick={run} disabled={busy} className="btn-primary w-full justify-center" data-testid="demo-firm-run">
              {busy ? <><Loader2 size={14} className="animate-spin" /> Building the book…</> : "Build the demo firm"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ k, v, tid }) => (
  <div className="flex items-baseline gap-2 flex-wrap">
    <span className="kpi-label shrink-0" style={{ minWidth: 112 }}>{k}</span>
    <span className="font-mono text-[12.5px] break-all" data-testid={tid}>{v}</span>
  </div>
);

export default function AdminPartners() {
  const [data, setData] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [view, setView] = useState("partners");

  const load = async () => {
    try { setData((await api.get("/partners")).data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const totals = useMemo(() => {
    const p = data?.partners || [];
    return {
      partners: p.length,
      clients: p.reduce((s, x) => s + (x.stats?.clients || 0), 0),
      funded: p.reduce((s, x) => s + (x.stats?.funded_usdt || 0), 0),
      awaiting: p.reduce((s, x) => s + (x.stats?.awaiting_usdt || 0), 0),
    };
  }, [data]);

  const copyPortal = (slug) => {
    navigator.clipboard?.writeText(`${window.location.origin}/p/${slug}`);
    toast.success("Portal link copied");
  };

  return (
    <div className="min-h-screen pb-16" data-testid="admin-partners-page">
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 pt-4">
        <div className="flex items-end justify-between gap-3 mb-5 fade-up">
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight leading-none">Partners</h1>
            <p className="text-sm text-ink-2 mt-2">
              Third-party organisations with their own clients. You set the commercials, they get a branded portal and a console.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 p-1 rounded-xl" style={{ background: "rgba(15,18,38,0.05)" }}>
              {[["partners", "Partners", Building2], ["pnl", "P&L", BarChart3]].map(([k, label, Icon]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`partners-view-${k}`}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 transition-colors"
                  style={view === k ? { background: "white", color: "#0F1226", boxShadow: "0 2px 8px rgba(15,18,38,0.08)" } : { color: "rgba(15,18,38,0.6)" }}>
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
            <a href="/admin/partners/users" className="btn-secondary" data-testid="partners-user-management"
              title="Partner console logins and their clients' logins — passwords, sign-in history, switch off">
              <KeyRound size={14} /> User management
            </a>
            <button onClick={() => setDemoOpen(true)} className="btn-secondary" data-testid="seed-demo-firm-btn"
              title="Build the demo firm with a full book you can show people">
              <Sparkles size={14} /> Demo firm
            </button>
            <button onClick={() => setCreateOpen(true)} className="btn-primary" data-testid="new-partner-btn"><Plus size={15} /> New partner</button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          {[["Partners", totals.partners, Building2], ["Their clients", totals.clients, Users],
            ["Funded capital", usd(totals.funded), Wallet], ["Awaiting funds", usd(totals.awaiting), Clock]].map(([k, v, Icon]) => (
            <div key={k} className="glass p-4" data-testid={`partners-kpi-${k.toLowerCase().replace(/\s/g, "-")}`}>
              <div className="flex items-center gap-2 kpi-label"><Icon size={12} /> {k}</div>
              <div className="font-display text-2xl font-semibold mt-1">{v}</div>
            </div>
          ))}
        </div>

        {view === "pnl" ? <PnlPanel /> : !data ? <div className="glass p-8 text-sm text-ink-2">Loading partners…</div>
          : !data.partners.length ? (
            <div className="glass-strong p-10 text-center" data-testid="partners-empty">
              <Building2 size={26} className="mx-auto mb-3 text-ink-3" />
              <div className="font-display text-xl font-semibold">No partners yet</div>
              <p className="text-sm text-ink-2 mt-2">Create the first one — you'll get a portal address and a login to hand over.</p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {data.partners.map((p) => (
                <div key={p.id} className="glass p-4 flex items-center gap-4 flex-wrap" data-testid={`partner-row-${p.slug}`}>
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${(p.branding?.accent || "#4060FF")}1f` }}>
                    {p.branding?.logo
                      ? <img src={p.branding.logo} alt="" style={{ width: 28, height: 28, objectFit: "contain" }} />
                      : <Building2 size={16} style={{ color: p.branding?.accent || "#4060FF" }} />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-[15px] flex items-center gap-2">
                      {p.name}
                      <span className={`chip ${p.status === "active" ? "chip-emerald" : "chip-rose"}`}>{p.status}</span>
                      {p.member_linked ? (
                        <span className="chip !text-[10.5px]" style={{ background: "rgba(194,24,91,0.10)", color: "#C2185B", fontWeight: 700 }}
                          title={`Runs on ${p.member.email}'s BotChainCP login`} data-testid={`partner-member-chip-${p.slug}`}>
                          {p.member.email}
                        </span>
                      ) : (
                        <span className="chip chip-amber !text-[10.5px]" data-testid={`partner-no-member-${p.slug}`}
                          title="This firm predates the member mapping — attach the member who runs it">
                          no member linked
                        </span>
                      )}
                      {p.suspended_by_member_toggle && (
                        <span className="chip chip-amber !text-[10.5px]" data-testid={`partner-toggle-suspended-${p.slug}`}
                          title="Suspended because Partner was unticked against their member — re-tick to bring it back">
                          partner switched off
                        </span>
                      )}
                    </div>
                    <div className="text-[12px] text-ink-3">/p/{p.slug} · partner keeps {p.terms?.margin_split_partner_pct}% of the margin · promise {p.terms?.promise_min_pct}–{p.terms?.promise_max_pct}%/mo</div>
                  </div>
                  <div className="flex items-center gap-5 ml-auto text-sm">
                    <span title="Clients"><b>{p.stats?.clients || 0}</b> <span className="text-ink-3">clients</span></span>
                    <span title="Awaiting review"><b>{p.stats?.applied || 0}</b> <span className="text-ink-3">to review</span></span>
                    <span title="Funded"><b>{usd(p.stats?.funded_usdt)}</b> <span className="text-ink-3">funded</span></span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => copyPortal(p.slug)} className="btn-secondary" data-testid={`copy-portal-${p.slug}`}><Copy size={13} /> Portal link</button>
                    <button onClick={() => setOpenId(p.id)} className="btn-primary" data-testid={`manage-partner-${p.slug}`}>Manage</button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </div>

      {createOpen && <CreateModal defaults={data?.defaults || { terms: {} }} onClose={() => setCreateOpen(false)} onCreated={load} />}
      {demoOpen && <DemoFirmModal onClose={() => setDemoOpen(false)} onSeeded={load} />}
      {openId && <DetailModal id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </div>
  );
}
